import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ContactPopup } from '../components/ContactPopup';
import { Toaster } from '../components/ui/sonner';
import { businessInfo } from '../mock';

export const TermsConditions = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const handleContactClick = () => {
    setIsPopupOpen(true);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Terms & Conditions | TradeMark Registry - Service Agreement</title>
        <meta name="description" content="Review our terms and conditions for trademark registration services. Understand your rights, responsibilities, payment terms, and service guarantees. Clear legal terms for USPTO filing services." />
        <meta name="keywords" content="terms and conditions, service agreement, trademark terms, legal terms, USPTO services terms, client responsibilities, service terms" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://trademark-pro-8.preview.emergentagent.com/terms-conditions" />
      </Helmet>
      
      <Header onContactClick={handleContactClick} />
      
      <main className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-[#3E2723] mb-4">
            Terms & Conditions
          </h1>
          <p className="text-[#8D6E63] mb-8">
            Last Updated: December 2025
          </p>

          <div className="prose prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#3E2723] mb-4">1. Acceptance of Terms</h2>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                Welcome to {businessInfo.name}. These Terms and Conditions ("Terms") govern your use of our website and trademark registration services. By accessing our website or engaging our services, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use our services.
              </p>
              <p className="text-[#8D6E63] leading-relaxed">
                We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to our website. Your continued use of our services after changes are posted constitutes acceptance of the modified Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#3E2723] mb-4">2. Description of Services</h2>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                {businessInfo.name} provides professional trademark registration services, including but not limited to:
              </p>
              <ul className="list-disc pl-6 text-[#8D6E63] space-y-2 mb-4">
                <li>Comprehensive trademark searches</li>
                <li>Trademark application preparation and filing with the USPTO</li>
                <li>Office action responses and legal representation</li>
                <li>Trademark monitoring and renewal services</li>
                <li>Brand protection consultation and strategy</li>
              </ul>
              <p className="text-[#8D6E63] leading-relaxed">
                Our services are limited to trademark matters in the United States and filing with the United States Patent and Trademark Office (USPTO).
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#3E2723] mb-4">3. Client Responsibilities</h2>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                As a client, you agree to:
              </p>
              <ul className="list-disc pl-6 text-[#8D6E63] space-y-2 mb-4">
                <li>Provide accurate, complete, and truthful information</li>
                <li>Respond promptly to requests for information or documentation</li>
                <li>Review all documents and filings before submission</li>
                <li>Make timely payments for services rendered</li>
                <li>Comply with all applicable laws and USPTO regulations</li>
                <li>Maintain the confidentiality of your account credentials</li>
                <li>Notify us immediately of any changes to your contact information</li>
                <li>Use your trademark in commerce as represented in your application</li>
              </ul>
              <p className="text-[#8D6E63] leading-relaxed">
                Failure to fulfill these responsibilities may result in delays, additional costs, or denial of your trademark application.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#3E2723] mb-4">4. Fees and Payment Terms</h2>
              
              <h3 className="text-xl font-semibold text-[#3E2723] mb-3">4.1 Service Fees</h3>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                Our service fees are clearly displayed on our website and vary depending on the package selected. Service fees are separate from government filing fees charged by the USPTO.
              </p>

              <h3 className="text-xl font-semibold text-[#3E2723] mb-3">4.2 Government Fees</h3>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                USPTO government filing fees are paid directly to the United States Patent and Trademark Office and are non-refundable. Current government fees range from $250-$350 per class of goods/services, subject to change by the USPTO.
              </p>

              <h3 className="text-xl font-semibold text-[#3E2723] mb-3">4.3 Payment</h3>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                Payment is due at the time services are requested unless otherwise agreed in writing. We accept major credit cards, debit cards, and electronic bank transfers. All payments must be made in U.S. dollars.
              </p>

              <h3 className="text-xl font-semibold text-[#3E2723] mb-3">4.4 Refund Policy</h3>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                Service fees may be refundable under certain circumstances if services have not yet been performed. Once your application has been filed with the USPTO, service fees are non-refundable. Government filing fees are never refundable. Please contact us to discuss specific refund requests.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#3E2723] mb-4">5. No Guarantee of Registration</h2>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                While we use our expertise to maximize the likelihood of successful trademark registration, we cannot guarantee that the USPTO will approve your trademark application. The USPTO has sole authority to grant or deny trademark registrations based on federal trademark law and examination guidelines.
              </p>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                Factors affecting registration success include:
              </p>
              <ul className="list-disc pl-6 text-[#8D6E63] space-y-2 mb-4">
                <li>Likelihood of confusion with existing marks</li>
                <li>Descriptiveness or genericness of the mark</li>
                <li>Compliance with USPTO filing requirements</li>
                <li>Third-party opposition proceedings</li>
                <li>Changes in trademark law or USPTO policies</li>
              </ul>
              <p className="text-[#8D6E63] leading-relaxed">
                Our services include professional preparation and filing of your application, but ultimate approval rests with the USPTO.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#3E2723] mb-4">6. Intellectual Property Rights</h2>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                All content on our website, including text, graphics, logos, images, and software, is the property of {businessInfo.name} or its licensors and is protected by copyright, trademark, and other intellectual property laws. You may not reproduce, distribute, modify, or create derivative works without our express written permission.
              </p>
              <p className="text-[#8D6E63] leading-relaxed">
                Your trademark remains your property. By using our services, you grant us permission to file your trademark application with the USPTO on your behalf and to use your trademark information as necessary to provide our services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#3E2723] mb-4">7. Confidentiality</h2>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                We maintain strict confidentiality regarding your personal and business information, except as required by law or necessary to provide our services. However, please note that information submitted to the USPTO in connection with your trademark application becomes part of the public record and is accessible to anyone.
              </p>
              <p className="text-[#8D6E63] leading-relaxed">
                We will not disclose your confidential information to third parties without your consent, except to service providers who assist us in delivering our services and who are bound by confidentiality obligations.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#3E2723] mb-4">8. Limitation of Liability</h2>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                To the maximum extent permitted by law, {businessInfo.name} shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, goodwill, or business opportunities, arising out of or related to your use of our services.
              </p>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                Our total liability to you for any claim arising from our services shall not exceed the amount of fees you paid to us for the specific service giving rise to the claim.
              </p>
              <p className="text-[#8D6E63] leading-relaxed">
                This limitation of liability applies regardless of the legal theory upon which the claim is based, whether in contract, tort, negligence, strict liability, or otherwise.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#3E2723] mb-4">9. Indemnification</h2>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                You agree to indemnify, defend, and hold harmless {businessInfo.name}, its officers, employees, and agents from any claims, damages, losses, liabilities, and expenses (including reasonable attorneys' fees) arising from:
              </p>
              <ul className="list-disc pl-6 text-[#8D6E63] space-y-2 mb-4">
                <li>Your use of our services</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any third-party rights, including intellectual property rights</li>
                <li>Any false or misleading information you provide to us</li>
                <li>Your failure to use your trademark as represented in your application</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#3E2723] mb-4">10. Termination</h2>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                Either party may terminate the service agreement with written notice. Upon termination:
              </p>
              <ul className="list-disc pl-6 text-[#8D6E63] space-y-2 mb-4">
                <li>You remain responsible for all fees incurred prior to termination</li>
                <li>We will provide you with all documents and materials in our possession related to your trademark</li>
                <li>Any pending USPTO proceedings will continue unless you provide written instructions to withdraw</li>
                <li>No refund will be issued for services already performed</li>
              </ul>
              <p className="text-[#8D6E63] leading-relaxed">
                We reserve the right to terminate services immediately if you breach these Terms or engage in fraudulent or illegal activities.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#3E2723] mb-4">11. Dispute Resolution</h2>
              
              <h3 className="text-xl font-semibold text-[#3E2723] mb-3">11.1 Informal Resolution</h3>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                Before filing any formal claim, you agree to contact us and attempt to resolve the dispute informally by sending written notice to {businessInfo.email}.
              </p>

              <h3 className="text-xl font-semibold text-[#3E2723] mb-3">11.2 Arbitration</h3>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                Any dispute arising from these Terms or our services that cannot be resolved informally shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association. Arbitration shall take place in Los Angeles County, California.
              </p>

              <h3 className="text-xl font-semibold text-[#3E2723] mb-3">11.3 Class Action Waiver</h3>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                You agree to resolve disputes with us on an individual basis and waive your right to participate in class action lawsuits or class-wide arbitration.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#3E2723] mb-4">12. Governing Law</h2>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                These Terms shall be governed by and construed in accordance with the laws of the State of California and the federal laws of the United States, without regard to conflict of law principles. Any legal action or proceeding shall be brought exclusively in the courts located in Los Angeles County, California.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#3E2723] mb-4">13. Disclaimer of Warranties</h2>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                Our services are provided "as is" and "as available" without warranties of any kind, either express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement. We do not warrant that our services will be uninterrupted, error-free, or secure.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#3E2723] mb-4">14. Force Majeure</h2>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                We shall not be liable for any failure or delay in performance due to circumstances beyond our reasonable control, including but not limited to acts of God, natural disasters, war, terrorism, labor disputes, government actions, or failures of third-party service providers or the USPTO.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#3E2723] mb-4">15. Severability</h2>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                If any provision of these Terms is found to be invalid, illegal, or unenforceable, the remaining provisions shall continue in full force and effect. The invalid provision shall be replaced with a valid provision that most closely reflects the intent of the original provision.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#3E2723] mb-4">16. Entire Agreement</h2>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                These Terms, together with our Privacy Policy, constitute the entire agreement between you and {businessInfo.name} regarding your use of our services and supersede all prior agreements and understandings, whether written or oral.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#3E2723] mb-4">17. Contact Information</h2>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                If you have questions or concerns about these Terms and Conditions, please contact us:
              </p>
              <div className="bg-[#EFEBE9] border-2 border-[#D7CCC8] rounded-lg p-6">
                <p className="text-[#3E2723] font-semibold mb-2">{businessInfo.name}</p>
                <p className="text-[#8D6E63]">{businessInfo.address}</p>
                <p className="text-[#8D6E63]">Email: {businessInfo.email}</p>
                <p className="text-[#8D6E63]">Phone: {businessInfo.phone}</p>
              </div>
            </section>

            <section className="mb-8 bg-[#EFEBE9] border-2 border-[#D7CCC8] rounded-lg p-6">
              <p className="text-[#8D6E63] leading-relaxed text-sm">
                <strong>Acknowledgment:</strong> By using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions. If you are entering into this agreement on behalf of a company or other legal entity, you represent that you have the authority to bind such entity to these Terms.
              </p>
            </section>
          </div>
        </div>
      </main>

      <Footer onContactClick={handleContactClick} />
      <ContactPopup isOpen={isPopupOpen} onClose={handleClosePopup} />
      <Toaster />
    </div>
  );
};
